import { useCallback } from "react";
import { Checkbox } from "@legal-500/legal500-design-system";

type SelectAllCheckboxProps = {
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
  "aria-label": string;
  disabled?: boolean;
};

/**
 * A tri-state checkbox: some-but-not-all selected reads as indeterminate. The design system's
 * Checkbox has no prop for that — it is a DOM property, not an attribute — so it is set through
 * the forwarded ref rather than a `useEffect` + `querySelector` reach into a wrapper div.
 */
export default function SelectAllCheckbox({
  checked,
  indeterminate,
  onChange,
  "aria-label": ariaLabel,
  disabled,
}: SelectAllCheckboxProps) {
  // A callback ref rather than useEffect: `indeterminate` is a DOM property with no attribute, and
  // React re-runs the callback whenever it changes.
  const setInput = useCallback(
    (input: HTMLInputElement | null) => {
      if (input) {
        input.indeterminate = indeterminate;
      }
    },
    [indeterminate],
  );

  return (
    <Checkbox
      ref={setInput}
      aria-label={ariaLabel}
      checked={checked}
      disabled={disabled}
      onChange={onChange}
    />
  );
}
